import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest, HttpMethod } from '@api/common';
import { queryKeys, showApiError } from '@api/queryClient';
import toastService from '@services/toastService';
import { Dict, ProductType } from '~types/types';

// ============================================================================
// Types
// ============================================================================

export interface ProductAlias {
  id?: string;
  code: string;
  name: string;
  isDeleted: boolean;
}

export interface Product {
  id: string;
  code: string;
  name: string;
  density: string;
  notes: string;
  productType: ProductType;
  customers: Dict<string>;
  tanks: Dict<string>;
  isEnabled: boolean;
  concentration: string;
  aliases: ProductAlias[];
  waybillDescription: string;
}

// ============================================================================
// API Functions
// ============================================================================

const API_ENDPOINT = 'products';

const fetchProducts = async (): Promise<Product[]> => {
  return apiRequest<void, Product[]>(HttpMethod.GET, API_ENDPOINT);
};

const fetchProduct = async (id: string): Promise<Product> => {
  return apiRequest<void, Product>(HttpMethod.GET, `${API_ENDPOINT}/${id}`);
};

const createProduct = async (product: Omit<Product, 'id'>): Promise<Product> => {
  return apiRequest<Omit<Product, 'id'>, Product>(HttpMethod.POST, API_ENDPOINT, product);
};

const updateProduct = async (product: Product): Promise<Product> => {
  return apiRequest<Product, Product>(HttpMethod.PUT, `${API_ENDPOINT}/${product.id}`, product);
};

const deleteProduct = async (id: string): Promise<void> => {
  return apiRequest<void, void>(HttpMethod.DELETE, `${API_ENDPOINT}/${id}`);
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch all products
 */
export const useProductsQuery = () => {
  return useQuery({
    queryKey: queryKeys.products.all,
    queryFn: fetchProducts,
  });
};

/**
 * Fetch a single product by ID
 */
export const useProductQuery = (id: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.products.detail(id!),
    queryFn: () => fetchProduct(id!),
    enabled: !!id,
  });
};

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Create a new product
 */
export const useCreateProductMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
      toastService.success({ title: 'save_succeeded' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};

/**
 * Update an existing product
 */
export const useUpdateProductMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
      toastService.success({ title: 'save_succeeded' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};

/**
 * Delete a product
 */
export const useDeleteProductMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
      toastService.success({ title: 'product.product_removed' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};
